import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Icon } from '@ui/Icon';
import ElementListRow from '@features/proposals/ElementListRow/ElementListRow';
import {
  ElementKind,
  type IElement,
  type Baseline,
  type OneOfElementListChildren,
} from '@shared/models/project.model';
import {
  getParentElement,
  getPathToElement,
} from '@shared/helpers/recursive-element.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';

const ICON_SIZE = 12;

type ActionElementRowProps = {
  element: OneOfElementListChildren;
  version: Baseline;
  onElementClick?: () => void;
};

export const ActionElementRow: React.FC<ActionElementRowProps> = ({
  element,
  version,
  onElementClick,
}) => {
  const { elementIconName, name, ancestorNames } = useMemo(() => {
    const categorySource =
      element.kind === ElementKind.Product
        ? getParentElement(version, element)
        : element;
    const category = getElementCategory(categorySource);
    const iconName = category
      ? getElementIconCategoryName(category.id)
      : 'other';

    const path = getPathToElement(version, element, false) ?? [];
    const ancestors = path.filter(
      (p): p is IElement => !projectGuards.is.buildingVersion(p),
    );
    const names = ancestors.map((a) => getElementName(a, version.products));

    return {
      elementIconName: iconName,
      name: getElementName(element, version.products),
      ancestorNames: names,
    };
  }, [element, version]);

  const tooltipTitle =
    ancestorNames.length > 0 ? ancestorNames.join(' › ') : '';

  return (
    <Tooltip
      title={tooltipTitle}
      placement="left"
      enterDelay={500}
      disableInteractive
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
        }}
      >
        <ElementListRow
          name={name}
          nameReadonly
          hideQuantity
          resizable={false}
          hideSurfaceBackground
          leftGap={8}
          depth={1}
          height={20}
          iconSlot={null}
          prefixIconSlots={[
            <Icon
              key="element-icon"
              category="elementCategory"
              icon={elementIconName ?? 'other'}
              size={ICON_SIZE}
            />,
          ]}
          onSelect={onElementClick}
        />
      </Box>
    </Tooltip>
  );
};
